'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Car, Search } from 'lucide-react';
import { LicensePlateInput } from '@/components/ui/license-plate-input';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useCartStore } from '@/stores/cart';
import { useVehicleHistoryStore } from '@/stores/vehicle-history';
import { useVehicleStore } from '@/stores/vehicle';
import { toast } from 'sonner';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

interface CatalogVehicleBannerProps {
  slug: string;
  /** Override the navigation target. Defaults to `/katalog/${slug}`. ktype param is appended automatically. */
  basePath?: string;
}

export function CatalogVehicleBanner({ slug, basePath }: CatalogVehicleBannerProps) {
  const { locale } = useLocale();
  // Historik: hopfälld på mobil 2026-09-03 (utfälld tog 122 px av 640 ovanför vecket och
  // sköt första produkten under det). Sedan 2026-10-01 alltid synlig men kompakt, se nedan.
  // Fordonsfiltrerade sökningar står för ~68 % av orderraderna — ingången ska ligga överst.
  const [plate, setPlate] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const router = useRouter();
  const setVehicle = useCartStore((state) => state.setVehicle);
  const addEntry = useVehicleHistoryStore((state) => state.addEntry);
  const setCurrentVehicle = useVehicleStore((state) => state.setVehicle);

  // Check store AFTER mount to avoid hydration mismatch
  useEffect(() => {
    if (useCartStore.getState().selectedVehicle) {
      setVisible(false);
    }
    return useCartStore.subscribe((state) => {
      setVisible(!state.selectedVehicle);
    });
  }, []);

  if (!visible) return null;

  const handleLookup = async () => {
    const cleanPlate = plate.replace(/\s+/g, '').toUpperCase();
    if (!cleanPlate || cleanPlate.length < 5) {
      toast.error(t(locale, 'vehicleBanner.invalidPlate'));
      return;
    }

    setLoading(true);
    try {
      const result = await api.lookupRegistration(cleanPlate, 'category');
      if (result.ktype && result.vehicle) {
        setCurrentVehicle(result.vehicle, cleanPlate);
        setVehicle(result.vehicle, cleanPlate, 'katalogbanner');
        addEntry(cleanPlate, result.ktype, result.vehicle);
        toast.success(t(locale, 'vehicleBanner.found', { manufacturer: result.vehicle.manufacturer, model: result.vehicle.model }));
        const target = basePath || `/katalog/${slug}`;
        const separator = target.includes('?') ? '&' : '?';
        router.push(`${target}${separator}ktype=${result.ktype}`);
      } else {
        toast.error(t(locale, 'vehicleBanner.notFound'));
      }
    } catch (error) {
      toast.error(t(locale, 'vehicleBanner.error', { error: error instanceof Error ? error.message : t(locale, 'vehicleBanner.unknownError') }));
    } finally {
      setLoading(false);
    }
  };

  // Fältet syns alltid sedan 2026-10-01, även på mobil. Den hopfällda raden ("Har du
  // regnumret? ⌄") krävde ett klick innan man kunde skriva, och ägaren frågade om man
  // förstår vad man ska göra. Kompakt (rubrik + fält på ~95 px) i stället för hopfälld.
  return (
    <div className="mb-6 rounded-xl border border-brand/20 bg-cream/40 p-3 sm:p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-brand mb-2">
        <Car className="h-4 w-4 shrink-0" aria-hidden />
        {t(locale, 'vehicleBanner.title')}
      </p>
      <form
        onSubmit={(e) => { e.preventDefault(); handleLookup(); }}
        className="flex gap-2"
      >
        <div className="flex-1 min-w-0 sm:max-w-xs">
          <LicensePlateInput
            value={plate}
            onChange={setPlate}
            onSubmit={handleLookup}
            loading={loading}
            size="sm"
          />
        </div>
        <Button type="submit" disabled={loading} size="default" className="shrink-0">
          <Search className="h-4 w-4 mr-1.5" />
          {t(locale, 'vehicleBanner.search')}
        </Button>
      </form>
    </div>
  );
}
